// @avantia common-code
import { ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { hasOwnProperty } from '@avantia/logging-and-errors';
import { HpAnswerMap } from '@avantia/questionset-model';
import { getAnswersFromAnswerMap } from '../engineClient/tools/clientAndServerUtilities';
import { HpAlertConditionsMap } from '../types/alertTypes';
import { AccidentalDamageLevel, CoverType, PolicyMap } from '../types/policyTypes';

function parsePolicyData(policyData: string | undefined): PolicyMap {
  const data = JSON.parse(ensureDefined(policyData, 'policyData')) as PolicyMap;
  return data;
}

export function getPolicyMapFromAnswers(answers: HpAnswerMap, productId: string): PolicyMap {
  const { policyData } = getAnswersFromAnswerMap({
    answers,
    keys: ['policyData'],
    options: { returnAnyAnswerType: true },
    productId
  });
  return parsePolicyData(policyData);
}

// TODO AL - This should come already mapped from Pas Interface
const coverTypeToAnswerMap: SMap<keyof CoverType> = Object.freeze({
  'Buildings & Contents': 'BuildingsAndContents',
  Contents: 'Contents',
  Buildings: 'Buildings'
});

export const coverTypeMap: { [key in keyof CoverType]: keyof CoverType } = {
  BuildingsAndContents: 'BuildingsAndContents',
  Contents: 'Contents',
  Buildings: 'Buildings'
};

export const accidentalDamageCoverLevelMap: {
  [key in keyof AccidentalDamageLevel]: keyof AccidentalDamageLevel;
} = {
  Basic: 'Basic',
  Full: 'Full',
  None: 'None'
};

export function getCoverTypeCode(coverTypeText: string): keyof CoverType {
  return ensureDefined(coverTypeToAnswerMap[coverTypeText], coverTypeText);
}

export interface AddonTypesRecord {
  HomeEmergency?: boolean;
  KeyProtect?: boolean;
  LegalExpenses?: boolean;
  RentGuarantee?: boolean;
}

export const addonTypes: AddonTypesRecord = {
  LegalExpenses: true,
  HomeEmergency: true,
  RentGuarantee: true,
  KeyProtect: true
};

export function isValidAddonTypeCode(key: string): boolean {
  const addon = key as keyof AddonTypesRecord;
  if (!hasOwnProperty(addonTypes, addon)) {
    throw new Error(`${addon} is not a valid addon type code.`);
  }

  return true;
}

export function filterPolicyInstances(policies: PolicyMap, selectedDate: Date | null): PolicyMap | null {
  const filteredPolicies = {};
  if (selectedDate) {
    for (const policyReference in policies) {
      const policy = policies[policyReference];
      const { startDate, endDate } = policy;
      if (selectedDate >= new Date(startDate) && selectedDate <= new Date(endDate)) {
        filteredPolicies[policyReference] = policy;
      }
    }
  }

  return filteredPolicies;
}

export interface HpClaimsCommonCustomData {
  copyText: HpClaimsCommonCopyText;
  alertConditionsMap: HpAlertConditionsMap;
}

export interface PolicyModalCopyText {
  coverDetails: HeadingAndBody;
  sumsInsured: HeadingAndBody;
  addons: HeadingAndBody;
  endorsements: HeadingAndBody;
  excesses: HeadingAndBody;
  specifiedItems: HeadingAndBody;
}

export interface CoverTable {
  primaryTableHeaders?: string[];
  secondaryTableHeaders?: string[];
}
export interface HeadingAndBody {
  body: string;
  heading: string;
  table?: CoverTable;
}
export interface HpClaimsCommonCopyText {
  alerts: HpClaimsAlertCopyText;
  accidentalDamage: HpClaimsADCopyText;
  policyCoverModal: PolicyModalCopyText;
}

export interface HpClaimsAlertCopyText {
  adBasicBuildingSummary: string;
  adItemNotCoveredSummary: string;
  noSpecifiedItemNotCoveredSummary: string;
  adAwayItemNotCoveredSummary: string;
  adBasicCoverSummary: string;
  adNoneItemNotCoveredSummary: string;
  adContentBasicSummary: string;
  adBasicBuilding: string;
  adItemNotCovered: string;
  noSpecifiedItemNotCovered: string;
  adAwayItemNotCovered: string;
  adNoneItemNotCovered: string;
}

export interface HpClaimsADCopyText {
  adBHeading: string;
  adClaimSubHeading: string;
  adClaimInfo: string;
  adWhatCoveredHeader: string;
  adNoCoverHeader: string;
  adCInfo: string;
  adCHeading: string;
}

export function getCopyTextFromCustomData(commonCustomData: HpClaimsCommonCustomData): HpClaimsCommonCopyText {
  const customData = commonCustomData as unknown as HpClaimsCommonCustomData;
  const copyText = getCustomDataSection<HpClaimsCommonCopyText>('copyText', customData);
  return copyText;
}

export function getCustomDataSection<ResultT>(
  sectionName: keyof HpClaimsCommonCustomData,
  customData: HpClaimsCommonCustomData
): ResultT {
  return ensureDefined(customData[sectionName], sectionName) as unknown as ResultT;
}
